import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { Card } from './Card'

// Modal — the shared overlay shell extracted from the hand-rolled dialogs
// scattered across the console. It fixes tall-content clipping at 360px: the
// overlay is itself a scroll container (`overflow-y-auto`, `items-start
// sm:items-center`) so a panel taller than the viewport scrolls instead of
// having its confirm button pushed off-screen, and the panel caps at
// `max-h-[90dvh] overflow-y-auto`.
//
// It renders a `Card` internally, so a migrating consumer DROPS its own
// `<Card>` and passes the inner `<h3>/<p>/<Button>` markup verbatim as
// `children`. Backdrop click (target === currentTarget) and `Esc` both call
// `onClose`. `z-50` sits above the fixed noise overlay (`index.css`,
// `z-index:0`).
//
// Deliberately minimal for this slice: NO focus-trap, NO portal, NO new
// dependency (both deferred).
export interface ModalProps {
  onClose: () => void // backdrop click + Esc (unless dismissible=false)
  size?: 'md' | 'lg' // → max-w-md (default) | max-w-lg
  labelledBy?: string // id of the panel heading, for aria-labelledby
  // When false, Esc + backdrop click do NOT close — the only dismissal is an
  // explicit in-panel action. Use for a one-time secret reveal (e.g. an API
  // key shown once) where a stray keypress/click would permanently lose it.
  dismissible?: boolean // default true
  children: ReactNode // panel body — callers pass their current dialog contents
}

export function Modal({ onClose, size = 'md', labelledBy, dismissible = true, children }: ModalProps) {
  // Esc-to-close — mirrors the hand-rolled keydown pattern in
  // components/VersionPicker.tsx, cleaned up on unmount. Skipped entirely when
  // not dismissible so a one-time reveal can't be lost to a stray Esc.
  useEffect(() => {
    if (!dismissible) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, dismissible])

  const maxWidth = size === 'lg' ? 'max-w-lg' : 'max-w-md'

  return (
    <div
      className="fixed inset-0 z-50 flex min-h-full items-start justify-center overflow-y-auto bg-black/70 p-4 sm:items-center"
      onClick={
        dismissible
          ? (e) => {
              if (e.target === e.currentTarget) onClose()
            }
          : undefined
      }
    >
      <Card
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        onClick={(e) => e.stopPropagation()}
        className={`max-h-[90dvh] w-full overflow-y-auto ${maxWidth}`}
      >
        {children}
      </Card>
    </div>
  )
}
